import {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  createContext,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  apiClient,
  clearTokenGetter,
  setTokenGetter,
  setAuthErrorHandler,
  clearAuthErrorHandler,
  isTokenGetterSet,
} from "@/services/api";

/**
 * Context to track whether the API client is ready for authenticated requests
 */
interface ApiReadyContextType {
  isApiReady: boolean;
}

const ApiReadyContext = createContext<ApiReadyContextType>({ isApiReady: false });

/**
 * Hook to check if the API client is ready for authenticated requests.
 * Use this in the `enabled` option of React Query hooks to prevent
 * premature API calls.
 */
export function useApiReady(): boolean {
  const context = useContext(ApiReadyContext);
  return context.isApiReady;
}

/**
 * Provider Props
 */
interface ApiProviderProps {
  children: ReactNode;
}

/**
 * ApiProvider Component
 *
 * Wraps components that need authenticated API access.
 * Initializes the API client with Auth0 token getter and provides
 * a context to track when the API is ready.
 *
 * Usage:
 * ```typescript
 * // In ProtectedRoute or App.tsx
 * <ApiProvider>
 *   <YourProtectedContent />
 * </ApiProvider>
 * ```
 */
export function ApiProvider({ children }: ApiProviderProps) {
  const { getAccessTokenSilently, isAuthenticated, isLoading, logout } = useAuth0();
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
  const [isApiReady, setIsApiReady] = useState(false);
  const hasInitializedRef = useRef(false);

  // Memoized logout handler
  const handleAuthError = useCallback(() => {
    console.log("Auth error detected, logging out user...");
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  // Create token getter function
  const tokenGetter = useMemo(() => {
    return async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience,
          },
          cacheMode: "on",
        });
        if (!token) {
          throw new Error("getAccessTokenSilently returned empty token");
        }
        return token;
      } catch (error) {
        console.error("Error getting access token:", error);
        throw error;
      }
    };
  }, [getAccessTokenSilently, audience]);

  // CRITICAL: Initialize token getter SYNCHRONOUSLY during render
  // This ensures it's available before any child hooks run
  if (isAuthenticated && !isLoading && !hasInitializedRef.current) {
    setTokenGetter(tokenGetter);
    setAuthErrorHandler(handleAuthError);
    hasInitializedRef.current = true;
    // Note: Can't call setIsApiReady here (during render), but the sync setup is what matters
  }

  // Use effect to update the ready state and handle cleanup
  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      // User logged out or auth is still loading
      if (hasInitializedRef.current) {
        clearTokenGetter();
        clearAuthErrorHandler();
        hasInitializedRef.current = false;
      }
      setIsApiReady(false);
      return;
    }

    // Ensure token getter is set (handles edge cases like hot reload)
    if (!isTokenGetterSet()) {
      setTokenGetter(tokenGetter);
      setAuthErrorHandler(handleAuthError);
      hasInitializedRef.current = true;
    }

    // Mark API as ready
    setIsApiReady(true);

    // Cleanup on unmount
    return () => {
      clearTokenGetter();
      clearAuthErrorHandler();
      hasInitializedRef.current = false;
      setIsApiReady(false);
    };
  }, [isAuthenticated, isLoading, tokenGetter, handleAuthError]);

  return (
    <ApiReadyContext.Provider value={{ isApiReady }}>
      {children}
    </ApiReadyContext.Provider>
  );
}

/**
 * useApi Hook
 *
 * Returns the API client. Should be used within an ApiProvider.
 * For checking if API is ready, use useApiReady() hook.
 */
export function useApi() {
  return apiClient;
}
